import React from 'react';
import { Label, Button, Progress } from 'reactstrap';

export interface MediaTileProps {
  label: string;
  iconClass: string;
  hasContent: boolean;
  disabled: boolean;
  /** When set, remove stays enabled even if upload is disabled (e.g. title required for upload only). */
  removeDisabled?: boolean;
  isRemoving: boolean;
  emptyHint: string;
  replaceHint: string;
  layout?: 'default' | 'portrait' | 'thumbnail';
  isDragActive?: boolean;
  isDragAccept?: boolean;
  isDragReject?: boolean;
  dragAcceptLabel?: string;
  dragRejectLabel?: string;
  getRootProps: () => React.HTMLAttributes<HTMLElement>;
  getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>;
  onRemoveClick?: () => void;
  isUploading?: boolean;
  uploadProgress?: number;
  uploadLabel?: string;
  children?: React.ReactNode;
}

const MediaTile: React.FC<MediaTileProps> = ({
  label,
  iconClass,
  hasContent,
  disabled,
  removeDisabled,
  isRemoving,
  emptyHint,
  replaceHint,
  layout = 'default',
  isDragActive = false,
  isDragAccept = false,
  isDragReject = false,
  dragAcceptLabel = 'Drop to upload',
  dragRejectLabel = 'Invalid file type',
  getRootProps,
  getInputProps,
  onRemoveClick,
  isUploading = false,
  uploadProgress = 0,
  uploadLabel = 'Uploading…',
  children,
}) => {
  const isPortrait = layout === 'portrait';
  const isThumbnail = layout === 'thumbnail';
  const tileWidth = isPortrait ? 240 : isThumbnail ? 280 : undefined;
  const removeButtonDisabled = (removeDisabled ?? disabled) || isRemoving;

  const showDragOverlay = !disabled && isDragActive;
  const dragRejected = showDragOverlay && isDragReject;
  const dragAccepted = showDragOverlay && isDragAccept && !isDragReject;

  const borderColor = dragRejected
    ? '#f06548'
    : dragAccepted
      ? '#0ab39c'
      : hasContent
        ? '#0ab39c'
        : '#ced4da';

  return (
    <div className={isPortrait || isThumbnail ? '' : 'h-100'} style={tileWidth ? { width: tileWidth, maxWidth: '100%' } : undefined}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Label className="mb-0 small fw-semibold">{label}</Label>
        {hasContent && onRemoveClick && (
          <Button
            type="button"
            color="link"
            size="sm"
            className="p-0 text-danger"
            disabled={removeButtonDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveClick();
            }}
            title={`Remove ${label.toLowerCase()}`}
          >
            {isRemoving ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>
                <i className="ri-delete-bin-line me-1" />
                Remove
              </>
            )}
          </Button>
        )}
      </div>
      <div
        {...getRootProps()}
        className="border border-2 rounded position-relative overflow-hidden"
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.65 : 1,
          borderStyle: hasContent && !showDragOverlay ? 'solid' : 'dashed',
          borderColor,
          backgroundColor: dragAccepted ? 'rgba(10, 179, 156, 0.06)' : dragRejected ? 'rgba(240, 101, 72, 0.06)' : hasContent ? '#f8fafc' : '#f8f9fa',
          minHeight: isPortrait ? undefined : isThumbnail ? 160 : 148,
          padding: isPortrait || isThumbnail ? 12 : undefined,
          transform: dragAccepted ? 'scale(1.02)' : 'scale(1)',
          transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
          boxShadow: dragAccepted
            ? '0 0 0 4px rgba(10, 179, 156, 0.2)'
            : dragRejected
              ? '0 0 0 4px rgba(240, 101, 72, 0.2)'
              : undefined,
        }}
      >
        <input {...getInputProps()} />
        <div
          style={{
            filter: dragAccepted ? 'blur(3px) brightness(0.92)' : undefined,
            transition: 'filter 0.2s ease',
            pointerEvents: showDragOverlay ? 'none' : undefined,
          }}
        >
          {hasContent ? (
            <div className={isPortrait ? 'text-center' : 'p-3 d-flex flex-column h-100'}>
              <div className={isPortrait ? '' : 'flex-grow-1 d-flex align-items-center justify-content-center'}>
                {children}
              </div>
              {!disabled && !showDragOverlay && (
                <p className="mb-0 text-muted text-center small mt-2">
                  <i className="ri-refresh-line me-1" />
                  {replaceHint}
                </p>
              )}
            </div>
          ) : (
            <div
              className="text-center d-flex flex-column align-items-center justify-content-center"
              style={
                isPortrait
                  ? { aspectRatio: '9 / 16', padding: 16 }
                  : isThumbnail
                    ? { minHeight: 140, padding: 16 }
                    : { padding: '2rem', minHeight: 148 }
              }
            >
              <i className={`${iconClass} fs-2 text-muted mb-2`} />
              <p className="mb-0 text-muted small">{emptyHint}</p>
            </div>
          )}
        </div>

        {isUploading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center px-3"
            style={{
              zIndex: 15,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
          >
            <div className="d-flex justify-content-between small mb-2">
              <span className="text-muted fw-semibold">{uploadLabel}</span>
              <span className="text-muted">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} color="primary" className="mb-2" style={{ height: 8 }} />
            <span className="text-center small text-muted">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Please keep this page open
            </span>
          </div>
        )}

        {showDragOverlay && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center px-3"
            style={{
              zIndex: 10,
              background: dragRejected
                ? 'rgba(255, 255, 255, 0.55)'
                : 'rgba(255, 255, 255, 0.45)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mb-2"
              style={{
                width: 52,
                height: 52,
                background: dragRejected ? 'rgba(240, 101, 72, 0.15)' : 'rgba(10, 179, 156, 0.15)',
                color: dragRejected ? '#f06548' : '#0ab39c',
              }}
            >
              <i className={`${dragRejected ? 'ri-close-circle-line' : 'ri-upload-cloud-2-line'} fs-3`} />
            </div>
            <span
              className="small fw-semibold"
              style={{ color: dragRejected ? '#f06548' : '#0ab39c' }}
            >
              {dragRejected ? dragRejectLabel : dragAcceptLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaTile;
